import { CheckIcon, XIcon } from "@heroicons/react/solid";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import DetailSkeleton from "../../Components/DetailSkeleton";
import { classNames, getErrorMessages } from "../../Utils";
import { CSButton } from "../../Components/CSButton";
import {
  requestUpdateServiceOrderStatus,
  requestGetBusinessServiceOrder,
  useAuthState,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { Button } from "../../Components/Button";
import Divider from "../../Components/Divider";
import { ModalConfirm } from "../../Components/ModalConfirm";
import DialogModal from "../../Components/DialogModal";
import { businessServiceOrderStatus } from "../../Constants";
import Badge from "../../Components/Badge";

export const BusinessServiceOrderDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const dispatch = useBusinessServiceDispatch();
  const {
    businessServiceOrder,
    businessServiceOrderLoading,
    businessServiceOrderError,
    serviceOrderStatusUpdate,
    serviceOrderStatusUpdateLoading,
    serviceOrderStatusUpdateError,
  } = useBusinessServiceState();

  const { user } = useAuthState();

  const [steps, setsteps] = useState([]);
  const [orderStatus, setorderStatus] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const totalPrice = useMemo(() => {
    const addOnsTotal =
      businessServiceOrder?.addOns?.items?.reduce((sum, addOn) => {
        return sum + addOn.addOn.price;
      }, 0) || 0;

    return (businessServiceOrder?.businessService?.price || 0) + addOnsTotal;
  }, [
    businessServiceOrder?.businessService?.price,
    businessServiceOrder?.addOns?.items,
  ]);

  useEffect(() => {
    requestGetBusinessServiceOrder(dispatch, { id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      businessServiceOrder?.businessService?.steps &&
      businessServiceOrder?.status
    ) {
      const statusOrder = businessServiceOrder.businessService.steps.map(
        (step) => step.status,
      );
      const currentStepIndex = statusOrder.indexOf(businessServiceOrder.status);

      const getStepStatus = (currentStep, targetStep) => {
        if (currentStep < targetStep) return "complete";
        if (currentStep === targetStep) return "current";
        return "upcoming";
      };

      const updatedSteps = businessServiceOrder.businessService.steps.map(
        (step, index) => ({
          ...step,
          value: step.status,
          status: getStepStatus(index, currentStepIndex),
        }),
      );

      setsteps(updatedSteps);
      setorderStatus(businessServiceOrder.status);
    }
  }, [businessServiceOrder]);

  useEffect(() => {
    if (serviceOrderStatusUpdate) {
      setShowSuccessModal(true);
    } else if (serviceOrderStatusUpdateError) {
      setShowErrorModal(true);
    }
  }, [serviceOrderStatusUpdate, serviceOrderStatusUpdateError]);

  const renderStep = (step, stepIdx) => {
    if (step.status === "current" && step.position !== steps.length) {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group" aria-current="step">
            <span className="h-9 flex items-center" aria-hidden="true">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-blue-600 rounded-full">
                <span className="h-2.5 w-2.5 bg-blue-600 rounded-full" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase text-blue-600">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (
      step.status === "complete" ||
      (step.status === "current" && step.position === steps.length)
    ) {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-blue-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full">
                <CheckIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.completeDescription || step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "failed") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-red-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-red-600 rounded-full">
                <XIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    return (
      <div>
        {stepIdx !== steps.length - 1 ? (
          <div
            className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
            aria-hidden="true"
          />
        ) : null}
        <div className="relative flex items-start group">
          <span className="h-9 flex items-center" aria-hidden="true">
            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 rounded-full"></span>
          </span>
          <span className="ml-4 min-w-0 flex flex-col">
            <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">
              {t(step.name)}
            </span>
            <span className="text-sm text-gray-500">{t(step.description)}</span>
          </span>
        </div>
      </div>
    );
  };

  if (!businessServiceOrder || businessServiceOrderError) {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            message={
              businessServiceOrderError?.message ||
              "businessServiceOrderNotFound"
            }
          />
        </div>
        <div className="bg-white p-4 rounded-lg">
          <CSButton />
        </div>
      </div>
    );
  }

  const getStatusDetails = (status) => {
    return businessServiceOrderStatus.find((item) => item.value === status);
  };

  return (
    <div className="flex flex-col gap-6">
      {serviceOrderStatusUpdateError && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(serviceOrderStatusUpdateError)}
        />
      )}
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description="serviceOrderStatusSuccessChanged"
      />
      <DialogModal
        show={showCancelConfirmModal}
        type="danger"
        title={t("cancelOrderConfirmTitle")}
        body={t("cancelOrderConfirmDescription")}
        confirmTitle="Yes"
        cancelTitle="No"
        onClose={() => {
          setShowCancelConfirmModal(false);
          setPendingStatus(null);
        }}
        onCancel={() => {
          setPendingStatus(null);
        }}
        onConfirm={() => {
          requestUpdateServiceOrderStatus(dispatch, {
            orderID: businessServiceOrder.id,
            status: pendingStatus,
          });
          setPendingStatus(null);
        }}
      />
      <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="lg:col-start-3 lg:col-span-1">
          {businessServiceOrderLoading ? (
            <div className="bg-white animate-pulse rounded-lg p-4 flex items-center">
              <div className="h-8 w-8 mr-2 bg-gray-300 rounded-full"></div>
              <div>
                <div className="h-4 w-24 my-1 bg-gray-300 rounded-md"></div>
                <div className="h-4 w-48 my-1 bg-gray-300 rounded-md"></div>
              </div>
            </div>
          ) : (
            <div>
              {user.membership === "CONSULTANT" && (
                <>
                  <div className="bg-white rounded-t-lg p-4">
                    <label
                      htmlFor="order-status"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("changeStatus")}
                    </label>
                    <div className="flex gap-4">
                      <select
                        id="order-status"
                        name="order-status"
                        onChange={(event) => {
                          setorderStatus(event.target.value);
                        }}
                        disabled={serviceOrderStatusUpdateLoading}
                        value={orderStatus}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500 text-sm"
                      >
                        {steps.length > 0
                          ? steps.map((step, index) => (
                              <option
                                key={index}
                                value={step.value}
                                disabled={
                                  index - 1 <
                                  steps.findIndex(
                                    (s) =>
                                      s.value === businessServiceOrder?.status,
                                  )
                                }
                              >
                                {step.name}
                              </option>
                            ))
                          : businessServiceOrderStatus
                              .filter(
                                (status) =>
                                  status.value === "PENDING" ||
                                  status.value === "CONSULTATION" ||
                                  status.value === "IN_PROGRESS" ||
                                  status.value === "FINISHED" ||
                                  status.value === "CANCELLED",
                              )
                              .map((step, index) => (
                                <option key={index} value={step.value}>
                                  {t(step.label)}
                                </option>
                              ))}
                      </select>
                      <Button
                        type="button"
                        loading={serviceOrderStatusUpdateLoading}
                        disabled={serviceOrderStatusUpdateLoading}
                        className="mt-1 w-full sm:w-auto"
                        title={t("Save")}
                        onClick={() => {
                          if (orderStatus === "CANCELLED") {
                            setPendingStatus(orderStatus);
                            setShowCancelConfirmModal(true);
                          } else {
                            requestUpdateServiceOrderStatus(dispatch, {
                              orderID: businessServiceOrder.id,
                              status: orderStatus,
                            });
                          }
                        }}
                        data-testid="business-service-checkout"
                      />
                    </div>
                  </div>
                  <Divider />
                </>
              )}
              {steps.length > 0 && (
                <div
                  className={classNames(
                    user.membership === "CONSULTANT"
                      ? "rounded-b-lg"
                      : "rounded-lg",
                    "bg-white p-4 flex flex-col gap-4",
                  )}
                >
                  {businessServiceOrder?.status && (
                    <ol className="overflow-hidden">
                      {steps.map((step, stepIdx) => (
                        <li
                          key={step.name}
                          className={classNames(
                            stepIdx !== steps.length - 1 ? "pb-10" : "",
                            "relative",
                          )}
                        >
                          {renderStep(step, stepIdx)}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg lg:col-start-1 lg:col-span-2">
          <div className="px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("serviceName")}
                </dt>
                {businessServiceOrderLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {businessServiceOrder.businessService?.name}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("orderDate")}
                </dt>
                <div className="text-sm font-bold text-gray-900">
                  {dayjs(businessServiceOrder.createdAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY HH:mm")}
                </div>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("businessEntityName")}
                </dt>
                {businessServiceOrderLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {businessServiceOrder.detail?.companyName || "-"}
                  </dd>
                )}
              </div>
              {businessServiceOrder?.city?.id && (
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("serviceCity")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {businessServiceOrder.city.name}
                  </dd>
                </div>
              )}
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Add-On")}
                </dt>
                {businessServiceOrderLoading ? (
                  <DetailSkeleton />
                ) : (
                  <div className="mt-1 text-sm text-gray-900">
                    {businessServiceOrder.addOns?.items.length > 0 ? (
                      <ul>
                        {businessServiceOrder.addOns?.items?.map((addon) => (
                          <li key={addon.addOn.id} className="flex">
                            -<span className="ml-1">{t(addon.addOn.name)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <dd className="mt-1 text-sm text-gray-900">-</dd>
                    )}
                  </div>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Total")}
                </dt>
                {businessServiceOrderLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {totalPrice && (
                      <NumericFormat
                        displayType={"text"}
                        value={totalPrice}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        prefix={"Rp"}
                      />
                    )}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Consultation")}
                </dt>
                {businessServiceOrderLoading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    <a
                      className="text-sm text-blue-600 underline"
                      href={`/consultation/${businessServiceOrder.consultationID}`}
                    >
                      {t("viewConsultation")}
                    </a>
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Status")}
                </dt>
                {businessServiceOrderLoading ? (
                  <DetailSkeleton />
                ) : (
                  (() => {
                    const statusDetails = getStatusDetails(
                      businessServiceOrder.status,
                    );
                    if (statusDetails) {
                      return (
                        <Badge
                          title={statusDetails.label}
                          type={statusDetails.type}
                        />
                      );
                    }
                    return <Badge title="Unknown Status" type="secondary" />;
                  })()
                )}
              </div>
            </dl>
          </div>
        </div>
      </div>
      <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="col-span-2 bg-white p-4 rounded-lg">
          <CSButton />
        </div>
      </div>
    </div>
  );
};
